"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Users, MousePointerClick, ArrowLeft, ArrowRight, CircleCheck as CheckCircle2, Sparkles, ChartBar as BarChart3, Globe } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const metrics = [{
  title: "Initial Traffic Velocity",
  value: "+30%",
  timeframe: "First 3 Months",
  detail: "Immediate uplift achieved after fixing critical crawl blocks and re-aligning local search intent.",
  icon: TrendingUp,
  badgeText: "Phase 1 Momentum"
}, {
  title: "Organic Traffic Expansion",
  value: "+100%",
  timeframe: "After Year 1",
  detail: "Doubled overall search traffic by scaling editorial content clusters from local into national search markets.",
  icon: Globe,
  badgeText: "12-Month Milestone"
}, {
  title: "Daily Reader Acquisition",
  value: "25",
  timeframe: "New Readers / Day",
  detail: "Consistent, compounding daily influx of engaged new readers discovering key editorial topics organically.",
  icon: Users,
  badgeText: "Audience Scale"
}, {
  title: "Search Result Clicks",
  value: "200",
  timeframe: "Monthly Direct Clicks",
  detail: "High-intent monthly organic clicks generated from targeted national queries without paid advertising spend.",
  icon: MousePointerClick,
  badgeText: "Search Volume"
}];
const pillars = [{
  heading: "Challenge & Diagnostic",
  text: "Audited indexation bottlenecks, duplicate URL taxonomy, and local queries limiting national visibility."
}, {
  heading: "Content & Silo Restructure",
  text: "Rebuilt information architecture, interlinked topic clusters, and mapped high-intent national search terms."
}, {
  heading: "Direct Technical Delivery",
  text: "Remediated schema markup, metadata hierarchy, and on-page performance firsthand with zero agency delegation."
}, {
  heading: "Compounding Growth",
  text: "Delivered sustainable, evergreen organic rankings resilient to ongoing Google search algorithm updates."
}];
export default function Results() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12,
        duration: prefersReducedMotion ? 0.01 : 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="casestudyresults" data-nav="light" className="relative w-full bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Top Back Navigation */}
        <div className="mb-8 flex items-center justify-between">
          <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground">
            <Link to="/CaseStudiesPage" className="inline-flex items-center gap-2">
              <ArrowLeft className="size-4" />
              <span>Back to All Case Studies</span>
            </Link>
          </Button>

          <Badge variant="secondary" className="px-3 py-1 font-medium text-foreground">
            <Sparkles className="size-3 text-accent mr-1" />
            Verified Case Outcomes
          </Badge>
        </div>

        {/* Section Heading */}
        <div className="max-w-3xl mb-16">
          <span className="text-xs font-semibold tracking-wider uppercase text-primary mb-2 block font-sans">
            Verified SEO Impact
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default mb-6">
            Results That Speak for Themselves: Local to National Reach
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            When an online publication can be discovered anywhere in the country via organic search, it unlocks scalable readership and commercial growth. By pairing localized relevance with nationwide topical authority, here is the verified performance delivered.
          </p>
        </div>

        {/* Key Metrics Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {metrics.map((metric, i) => {
          const Icon = metric.icon;
          return <motion.div key={metric.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <Icon className="size-5" />
                      </div>
                      <Badge variant="outline" className="text-2xs font-normal border-border">
                        {metric.badgeText}
                      </Badge>
                    </div>
                    <CardTitle className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                      {metric.value}
                    </CardTitle>
                    <CardDescription className="text-xs font-medium text-primary uppercase tracking-wide">
                      {metric.timeframe}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {metric.detail}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Implementation Breakdown Banner */}
        <div className="rounded-xl border border-border bg-card/50 p-6 md:p-10 mb-16 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 text-primary text-sm font-semibold mb-3">
                <BarChart3 className="size-4" />
                <span>The Implementation Process</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4 font-default">
                How this outcome was achieved
              </h3>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-6">
                I systematically diagnosed and resolved critical indexing barriers, restructured the URL taxonomy and topic clusters for national search intent, and executed direct on-page technical optimizations to build compounding organic visibility.
              </p>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-accent" />
                  <span>Direct consultant execution with zero delegation</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-accent" />
                  <span>Verified Google Search Console data sets</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-accent" />
                  <span>Sustainable growth architecture built for the long term</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {pillars.map((pillar, index) => <div key={pillar.heading} data-index={index} className="p-5 rounded-lg bg-background border border-border/80">
                    <span className="text-xs font-bold text-primary mb-1 block">
                      {"0" + (index + 1)}
                    </span>
                    <h4 className="font-semibold text-foreground text-base mb-2">
                      {pillar.heading}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {pillar.text}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        <Separator className="mb-14" />

        {/* CTA Card */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-primary text-primary-foreground p-8 md:p-12 rounded-xl shadow-lg">
          <div className="max-w-xl text-center md:text-left">
            <span className="inline-block text-xs uppercase tracking-wider font-semibold text-accent mb-2">
              Ready for Sustainable Search Growth?
            </span>
            <h3 className="text-2xl md:text-3xl font-bold font-default tracking-tight mb-2">
              Work Directly With Me On Your SEO Strategy
            </h3>
            <p className="text-primary-foreground/80 text-sm md:text-base">
              Book a straightforward, free 30-minute consultation. We will evaluate your organic search performance and map out high-impact growth steps.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto shrink-0">
            <Button variant="secondary" size="lg" asChild className="w-full sm:w-auto font-semibold shadow-sm">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="w-full sm:w-auto bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
              <Link to="/CaseStudiesPage">
                <span>Explore Other Case Studies</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}